import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/joy";
import { Button } from "@mui/material";
import { AddRounded } from "@mui-symbols-material/w300";
import { FilteredSearch, LoadingScreen } from "@dreebit-registry/vsm-microservice-components";
import { InstalledBaseNodeSubscriptions } from "../../graphql/queries/installedBaseNodeSubscriptions";
import { InstalledBaseRegionSubscriptions } from "../../graphql/queries/installedBaseRegionSubscriptions";
import useRegionSubscriptionAccess from "../../hooks/useRegionSubscriptionAccess";
import useSlaSubscriptionSettings from "../../hooks/useSlaSubscriptionSettings";
import NodeSubscriptionModal from "../NodeSubscriptionModal";
import NotificationSubscriptionCard from "../NotificationSubscriptionCard";
import DeleteSubscriptionModal from "../DeleteSubscriptionModal";
import DeleteRegionSubscriptionModal from "../DeleteRegionSubscriptionModal";
import NotificationSubscriptionsEmptyIllustration from "../NotificationSubscriptionsEmptyIllustration";
import { IotAlertSeverity, IotMaintenanceType } from "../../codegen/graphql";

interface SubscriptionRow {
    kind: "node" | "region";
    id: string;
    inApp: boolean;
    mail: boolean;
    push: boolean;
    iotAlert: boolean;
    iotAlertSeverity?: IotAlertSeverity | null;
    maintenanceDue: boolean;
    maintenanceSeverity: IotAlertSeverity | null;
    maintenanceType: IotMaintenanceType | null;
    /** Node id for node rows, region id for region rows. */
    targetId: string;
    title: string;
    breadcrumb: string;
    insert?: string | null;
    update?: string | null;
}

const CREATE_BUTTON_SX = {
    color: "#333333",
    backgroundColor: "#FDDDCD",
    "&:hover": { backgroundColor: "#FFBA99" },
};

const NotificationSubscriptionsList = () => {
    const { t } = useTranslation();

    const { data, loading } = useQuery(InstalledBaseNodeSubscriptions, {
        fetchPolicy: "cache-and-network",
    });

    // Always fetched (not admin-gated): existing region subscriptions must stay
    // visible/removable even if the user has lost the admin role meanwhile.
    const { data: regionSubsData, loading: regionSubsLoading } = useQuery(InstalledBaseRegionSubscriptions, {
        fetchPolicy: "cache-and-network",
    });

    // Gates the region tab in the add modal (global/region admins with regions).
    const { showRegionUi, regions } = useRegionSubscriptionAccess();

    // SLA tag on the cards (global/region admins with the SLA flag only).
    const { slaById } = useSlaSubscriptionSettings();

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editNodeId, setEditNodeId] = useState<string | undefined>(undefined);
    const [editRegionId, setEditRegionId] = useState<string | undefined>(undefined);
    const [deleteRow, setDeleteRow] = useState<SubscriptionRow | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");

    const rows: SubscriptionRow[] = useMemo(() => {
        const regionSubs = ((regionSubsData?.installedBaseRegionSubscriptionList?.subscriptions ?? []) as any[])
            .filter((sub) => !!sub?.region?.id)
            .map((sub): SubscriptionRow => ({
                kind: "region",
                id: sub.id,
                inApp: !!sub.inApp,
                mail: !!sub.mail,
                push: !!sub.push,
                iotAlert: !!sub.iotAlert,
                iotAlertSeverity: sub.iotAlertSeverity,
                maintenanceDue: !!sub.maintenanceDue,
                maintenanceSeverity: sub.maintenanceSeverity ?? null,
                maintenanceType: sub.maintenanceType ?? null,
                targetId: String(sub.region.id),
                title: sub.region.name ?? "",
                breadcrumb: "",
                insert: sub.insert,
                update: sub.update,
            }));

        const nodeSubs = ((data?.installedBaseNodeSubscriptionList?.subscriptions ?? []) as any[])
            .filter((sub) => !!sub?.installedBaseNode?.id)
            .map((sub): SubscriptionRow => {
                const node = sub.installedBaseNode;
                const path = (node.pathNodes ?? [])
                    .map((p: any) => p?.title)
                    .filter(Boolean);
                return {
                    kind: "node",
                    id: sub.id,
                    inApp: !!sub.inApp,
                    mail: !!sub.mail,
                    push: !!sub.push,
                    iotAlert: !!sub.iotAlert,
                    iotAlertSeverity: sub.iotAlertSeverity,
                    maintenanceDue: !!sub.maintenanceDue,
                    maintenanceSeverity: sub.maintenanceSeverity ?? null,
                    maintenanceType: sub.maintenanceType ?? null,
                    targetId: node.id,
                    title: node.title ?? "",
                    breadcrumb: path.join(" / "),
                    insert: sub.insert,
                    update: sub.update,
                };
            });

        // Region subscriptions first — they cover the broader scope.
        return [...regionSubs, ...nodeSubs];
    }, [data, regionSubsData]);

    const filteredRows = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(
            (r) =>
                r.title.toLowerCase().includes(q) ||
                r.breadcrumb.toLowerCase().includes(q)
        );
    }, [rows, searchQuery]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0 }}>
            <div
                className={"flex-row"}
                style={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 8px 13px 8px",
                    gap: 12,
                }}
            >
                <FilteredSearch
                    value={searchQuery}
                    defaultFields={[]}
                    onSearch={(v: any) => setSearchQuery(v?.query ?? "")}
                />
                <Button
                    variant="contained"
                    startIcon={<AddRounded />}
                    onClick={() => setAddModalOpen(true)}
                    sx={CREATE_BUTTON_SX}
                >
                    {t("Add")}
                </Button>
            </div>

            {(loading || regionSubsLoading) && rows.length === 0 ? (
                <LoadingScreen />
            ) : filteredRows.length === 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: "48px" }}>
                    <NotificationSubscriptionsEmptyIllustration
                        variant={searchQuery ? "search" : "bell"}
                        width={200}
                    />
                    <Typography level="body-md" sx={{ pt: "16px", textAlign: "center", color: "text.secondary" }}>
                        {searchQuery
                            ? t("No subscriptions match your search.")
                            : t("You have no notification subscriptions yet.")}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ overflowY: "auto", flex: 1, px: "8px", pb: "8px" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {filteredRows.map((row) => (
                            <NotificationSubscriptionCard
                                key={`${row.kind}-${row.id}`}
                                subscription={{
                                    id: row.id,
                                    title: row.title,
                                    breadcrumb: row.breadcrumb,
                                    inApp: row.inApp,
                                    mail: row.mail,
                                    push: row.push,
                                    iotAlert: row.iotAlert,
                                    iotAlertSeverity: row.iotAlertSeverity,
                                    maintenanceDue: row.maintenanceDue,
                                    maintenanceSeverity: row.maintenanceSeverity,
                                    maintenanceType: row.maintenanceType,
                                    slaStatusChange: slaById[row.kind][row.id],
                                    insert: row.insert,
                                    update: row.update,
                                    isRegion: row.kind === "region",
                                }}
                                onEdit={() =>
                                    row.kind === "region"
                                        ? setEditRegionId(row.targetId)
                                        : setEditNodeId(row.targetId)
                                }
                                onDelete={() => setDeleteRow(row)}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {addModalOpen && (
                <NodeSubscriptionModal
                    open={addModalOpen}
                    onClose={() => setAddModalOpen(false)}
                    allowNodePicker
                    allowRegionPicker={showRegionUi}
                    regions={regions}
                />
            )}
            {editNodeId && (
                <NodeSubscriptionModal
                    open={!!editNodeId}
                    onClose={() => setEditNodeId(undefined)}
                    installedBaseNodeId={editNodeId}
                />
            )}
            {editRegionId && (
                <NodeSubscriptionModal
                    open={!!editRegionId}
                    onClose={() => setEditRegionId(undefined)}
                    regionId={editRegionId}
                />
            )}
            {deleteRow && deleteRow.kind === "node" && (
                <DeleteSubscriptionModal
                    open={Boolean(deleteRow)}
                    subscriptionId={deleteRow.id}
                    nodeTitle={deleteRow.title}
                    onClose={() => setDeleteRow(null)}
                />
            )}
            {deleteRow && deleteRow.kind === "region" && (
                <DeleteRegionSubscriptionModal
                    open={Boolean(deleteRow)}
                    subscriptionId={deleteRow.id}
                    regionName={deleteRow.title}
                    onClose={() => setDeleteRow(null)}
                />
            )}
        </Box>
    );
};

export default NotificationSubscriptionsList;
